import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../utils/api';
import { FRAMEWORK_TRACKS, PRODUCTION_TRACK } from '../config/tracksCatalog';
import TrackCard, { TrackStatus } from './TrackCard';

const panel: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 22,
};

interface TopicProgressEntry {
  completed: number;
  completedChallenges: string[];
}

/**
 * Frameworks catalog -- the entry point into the 4 agent-framework tracks
 * (LangChain/LangGraph/CrewAI/Microsoft Agent Framework), replacing what was
 * previously a dead end: 39 real lessons with no UI that could reach them.
 * See docs/build-plan/03-core-learning-app.md's Frameworks Catalog section.
 */
const FrameworksCatalog: React.FC = () => {
  const navigate = useNavigate();
  const [topicProgress, setTopicProgress] = useState<Record<string, TopicProgressEntry>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    API.get('/progress')
      .then((response: any) => {
        if (cancelled) return;
        setTopicProgress(response?.[0]?.topicProgress || {});
      })
      .catch(() => { /* fail open to empty progress, not a broken page */ })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const statusFor = (completed: number, total: number): TrackStatus =>
    completed >= total && total > 0 ? 'complete' : completed > 0 ? 'in-progress' : 'not-started';

  const anyFrameworkStarted = FRAMEWORK_TRACKS.some((t) => (topicProgress[t.slug]?.completed || 0) > 0);

  // Determine the next recommended track for the user
  const recommendedTrack =
    FRAMEWORK_TRACKS.find((t) => {
      const progress = topicProgress[t.slug];
      const completed = progress?.completed || 0;
      return completed < t.lessons.length;
    }) || FRAMEWORK_TRACKS[0];

  const recCompleted = topicProgress[recommendedTrack.slug]?.completed || 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 1060 }}>
      {/* Header Panel with Roadmap Orientation */}
      <div className="ws-fade-up" style={{ ...panel, background: 'var(--surface)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span className="ws-mono" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ember-strong)' }}>
            Curriculum Roadmap
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>&middot;</span>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>10 Tracks &middot; 87 Framework Lessons</span>
        </div>

        <h1 className="ws-display" style={{ margin: '0 0 8px', fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>
          Interactive AI Agent Frameworks
        </h1>
        <p style={{ margin: '0 0 18px', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, maxWidth: 780 }}>
          Master modern agent engineering from the ground up: transition from prompt loops to production state graphs, autonomous multi-agent teams, and cloud runtimes.
        </p>

        {/* Guided 4-Step Pathway Stepper */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 10,
            background: 'var(--surface-sunken)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: 12,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="ws-mono" style={{ fontSize: 10, fontWeight: 700, color: 'var(--ember-strong)', letterSpacing: '0.05em' }}>STAGE 1 &middot; FOUNDATIONS</span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>1. LangChain &rarr; 2. LangGraph</span>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Agent harnesses, state graphs, memory</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="ws-mono" style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', letterSpacing: '0.05em' }}>STAGE 2 &middot; MULTI-AGENT</span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>3. CrewAI &rarr; 4. Microsoft MAF</span>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Specialist teams, flows, group chats</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="ws-mono" style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', letterSpacing: '0.05em' }}>STAGE 3 &middot; NATIVE SDKs</span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>5. Strands &rarr; 6. OpenAI &rarr; 7. ADK</span>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>MCP tools, tripwires, automatic delegation</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="ws-mono" style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', letterSpacing: '0.05em' }}>STAGE 4 &middot; PRODUCTION</span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>10. Tracing, Evals & Guardrails</span>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>OTel, RAGAS, deterministic validators</span>
          </div>
        </div>

        {/* Highlighted Next Action Banner */}
        <div
          style={{
            marginTop: 16,
            padding: '12px 16px',
            background: 'oklch(0.68 0.18 48 / 0.08)',
            border: '1px solid var(--ember)',
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 20 }}>👉</span>
            <div>
              <span className="ws-mono" style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--ember-strong)', letterSpacing: '0.05em' }}>
                Recommended Action: {recCompleted > 0 ? 'Continue' : 'Start With'} Track {String(recommendedTrack.trackNumber).padStart(2, '0')}
              </span>
              <p style={{ margin: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>
                {recommendedTrack.name}: {recommendedTrack.description}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate(`/tracks/${recommendedTrack.slug}`)}
            className="btn-live ws-display"
            style={{
              background: 'var(--ember)',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {recCompleted > 0 ? `Resume Lesson ${recCompleted + 1}` : 'Start Track 01'} &rarr;
          </button>
        </div>

        {loading && <p className="ws-mono" style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--text-tertiary)' }}>Syncing progress&hellip;</p>}
      </div>

      {/* Stage 1: Core Frameworks (Tracks 1 - 4) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <div>
            <h2 className="ws-display" style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>
              Stage 1 & 2: Core Frameworks & Multi-Agent Teams
            </h2>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              Follow this numbered sequence: start with single-agent harnesses, advance to state graphs, then multi-agent teams.
            </span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {FRAMEWORK_TRACKS.slice(0, 4).map((track) => {
            const progress = topicProgress[track.slug];
            const completed = progress?.completed || 0;
            const total = track.lessons.length;
            return (
              <TrackCard
                key={track.slug}
                track={track}
                status={statusFor(completed, total)}
                completed={completed}
                total={total}
                onClick={() => navigate(`/tracks/${track.slug}`)}
              />
            );
          })}
        </div>
      </div>

      {/* Stage 3: Specialized Agent SDKs (Tracks 5 - 9) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <div>
            <h2 className="ws-display" style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>
              Stage 3: Ecosystem SDKs & Native Tool Loops
            </h2>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              Direct SDKs for model-driven loops, MCP servers, and cloud-native runtimes.
            </span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {FRAMEWORK_TRACKS.slice(4).map((track) => {
            const progress = topicProgress[track.slug];
            const completed = progress?.completed || 0;
            const total = track.lessons.length;
            return (
              <TrackCard
                key={track.slug}
                track={track}
                status={statusFor(completed, total)}
                completed={completed}
                total={total}
                onClick={() => navigate(`/tracks/${track.slug}`)}
              />
            );
          })}
        </div>
      </div>

      {/* Stage 4: Production & Observability (Track 10) */}
      <div
        className="ws-fade-up"
        role="button"
        tabIndex={0}
        onClick={() => navigate('/production')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/production'); } }}
        style={{
          ...panel,
          background: 'var(--ink)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          border: '1px solid var(--border-strong)',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span
              className="ws-mono"
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.08em',
                padding: '2px 6px',
                borderRadius: 4,
                background: 'oklch(1 0 0 / 0.12)',
                color: 'var(--ember)',
              }}
            >
              TRACK 10
            </span>
            <span className="ws-mono" style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: anyFrameworkStarted ? 'var(--ember)' : 'var(--text-on-dark-dim)' }}>
              {anyFrameworkStarted ? 'Unlocked &middot; 12 Lessons' : 'Recommended Final Capstone'}
            </span>
          </div>
          <h3 className="ws-display" style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700, color: 'var(--text-on-dark)' }}>
            Stage 4: {PRODUCTION_TRACK.name}
          </h3>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-on-dark-dim)', maxWidth: 640, lineHeight: 1.55 }}>
            {PRODUCTION_TRACK.whyItMatters}
          </p>
        </div>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ember)', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
          Enter Track 10 &rarr;
        </span>
      </div>

      <p style={{ margin: 0, fontSize: 12, lineHeight: 1.6, color: 'var(--text-tertiary)', textAlign: 'center' }}>
        Whiskey&rsquo;s own agent backend runs on LangGraph.js -- you&rsquo;re learning the exact kind of stack running this app.
      </p>
    </div>
  );
};

export default FrameworksCatalog;
